// src/components/topbar/LanguageFlagButton.tsx
//
// Topbar control that opens the language picker. Lives next to
// LocationStatusBadge in AppShell — a single tap to swap
// languages mid-session.
//
// What renders:
//   - An IconButton with a small chip showing the FIRST grapheme
//     of the user's currently-selected language's native name
//     (e.g. "中", "ع", "हि") OR a 2-letter ISO code as fallback.
//   - On tap: a Drawer slides up from the bottom (mobile) /
//     in from the right (desktop) containing the
//     LanguagePickerSheet. Selecting a language persists +
//     closes; if generation is needed for the new language, the
//     LanguageGate's standby flow will kick in on the next
//     render.
//
// Why an abstract glyph instead of a country flag:
//   - Languages don't map cleanly to flags (Spanish in many
//     countries, Mandarin spans regions, Arabic spans many).
//   - Flag emoji rendering is uneven across browsers / devices.
//   - The glyph approach works in any script.

import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Box,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useLanguagePreference } from '../../i18n/useLanguagePreference';
import { LanguagePickerSheet } from '../i18n/LanguagePickerSheet';
import { useT } from '../../i18n/useT';

export const LanguageFlagButton: React.FC = () => {
  const t = useT();
  const { language, englishName, setLanguage } = useLanguagePreference();
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const glyph =
    (language?.nativeName && firstGlyph(language.nativeName)) ||
    (language?.code && language.code.toUpperCase().slice(0, 2)) ||
    '🌐';

  const label = englishName
    ? `Change language (currently ${englishName})`
    : 'Choose language';

  return (
    <>
      <Tooltip title={label} arrow>
        <IconButton
          onClick={() => setOpen(true)}
          size="medium"
          aria-label={label}
          sx={{
            width: 36,
            height: 36,
            color: 'primary.main',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 22,
            }}
            dir={language?.rtl ? 'rtl' : 'ltr'}
          >
            {glyph}
          </Box>
        </IconButton>
      </Tooltip>

      <Drawer
        anchor={isDesktop ? 'right' : 'bottom'}
        open={open}
        onClose={() => setOpen(false)}
        // Big rounded top corners on mobile makes it read as a
        // sheet rather than a full page.
        PaperProps={{
          sx: {
            bgcolor: '#FAFAF7',
            backgroundImage:
              'radial-gradient(ellipse 60% 40% at 20% 15%, rgba(15,76,117,0.06), transparent 70%), radial-gradient(ellipse 50% 35% at 85% 90%, rgba(224,122,95,0.07), transparent 70%)',
            borderTopLeftRadius: isDesktop ? 0 : 24,
            borderTopRightRadius: isDesktop ? 0 : 24,
            maxHeight: isDesktop ? '100%' : '90vh',
            width: isDesktop ? 480 : '100%',
            overflow: 'auto',
          },
        }}
      >
        <LanguagePickerSheet
          title={t("Change language")}
          subtitle={t("The app will switch as soon as the new language is ready. Translations are generated once and reused — fast on every visit after the first.")}
          current={language}
          onSelect={(entry) => {
            setLanguage(entry);
            setOpen(false);
          }}
        />
      </Drawer>
    </>
  );
};

/** Best-effort first-grapheme helper. Strips combining marks so
 *  we don't show half a character. */
function firstGlyph(s: string): string {
  const cleaned = s.normalize('NFC');
  if (cleaned.length === 0) return '';
  const Seg = (Intl as any).Segmenter;
  if (typeof Seg === 'function') {
    try {
      const seg = new Seg(undefined, { granularity: 'grapheme' });
      const it = seg.segment(cleaned)[Symbol.iterator]();
      const first = it.next();
      if (!first.done) return first.value.segment;
    } catch {
      /* ignore */
    }
  }
  return cleaned.charAt(0);
}
